import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Search, ChevronRight, Calendar, Filter, SortAsc } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProjectCard from '../components/ProjectCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useApi } from '../hooks/useApi';
import { categoriesAPI, projectsAPI } from '../api/client';

// Normalize text: remove accents and convert to lowercase
const normalize = (text) => {
  if (!text) return '';
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
};

const SubcategoryPage = () => {
  const { type, subcategoryId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [projects, setProjects] = useState([]);

  const { data: categories, loading: categoriesLoading } = useApi(categoriesAPI.getAll);
  
  const { 
    data: initialProjects = [], 
    loading: projectsLoading, 
    error: projectsError, 
    refetch: refetchProjects 
  } = useApi(
    () => projectsAPI.getBySubcategory(type, subcategoryId), 
    [type, subcategoryId]
  );

  useEffect(() => {
    setProjects(initialProjects);
  }, [initialProjects]);

  const category = categories?.[type];
  const subcategory = category?.subcategories.find(sub => sub.id === subcategoryId);

  // Local accent-insensitive search + sort
  const filteredAndSortedProjects = useMemo(() => {
    if (!projects) return [];

    let filtered = projects;
    const term = normalize(searchTerm.trim());

    if (term) {
      filtered = projects.filter(p => {
        const fields = [
          p.title,
          p.author,
          p.description,
          p.results,
          ...(p.keywords || [])
        ];
        return fields.some(f => normalize(f).includes(term));
      });
    }

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'author':
          comparison = a.author.localeCompare(b.author);
          break;
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [projects, searchTerm, sortBy, sortOrder]);

  if (categoriesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" text="Cargando información..." />
        </div>
      </div>
    );
  }

  if (!category || !subcategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Subcategoría no encontrada</h1>
          <Link to="/">
            <Button>Volver al inicio</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8 flex-wrap">
          <Link to="/" className="hover:text-emerald-600 transition-colors">Inicio</Link>
          <ChevronRight className="w-4 h-4" />
          <Link 
            to={`/${type === 'digital' ? 'recursos-digitales' : type === 'printed' ? 'recursos-impresos' : 'sitios-interes'}`} 
            className="hover:text-emerald-600 transition-colors"
          >
            {category.title}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">{subcategory.name}</span>
        </div>

        {/* Back Button */}
        <Button asChild variant="ghost" className="mb-6 hover:bg-emerald-50 hover:text-emerald-700">
          <Link 
            to={`/${type === 'digital' ? 'recursos-digitales' : type === 'printed' ? 'recursos-impresos' : 'sitios-interes'}`} 
            className="inline-flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver a {category.title}</span>
          </Link>
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {subcategory.name}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {subcategory.description}
          </p>
          <div className="mt-4 inline-flex items-center space-x-4 text-sm text-gray-500">
            <span className="inline-flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>{filteredAndSortedProjects.length} proyectos disponibles</span>
            </span>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Buscar por título, autor o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="subcategory-search-input"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40">
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4" />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Fecha</SelectItem>
                  <SelectItem value="title">Título</SelectItem>
                  <SelectItem value="author">Autor</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3"
              >
                <SortAsc className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {projectsError && !searchTerm && (
          <ErrorMessage error={projectsError} onRetry={refetchProjects} />
        )}
        
        {projectsLoading ? (
          <LoadingSpinner text="Cargando proyectos..." />
        ) : filteredAndSortedProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {filteredAndSortedProjects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                categoryName={subcategory.name}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2" data-testid="no-results-message">
              No se encontraron proyectos
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm 
                ? `No hay proyectos que coincidan con "${searchTerm}"` 
                : 'No hay proyectos disponibles en esta categoría'
              }
            </p>
            {searchTerm && (
              <Button 
                variant="outline" 
                onClick={() => setSearchTerm('')}
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                data-testid="clear-search-btn"
              >
                Limpiar búsqueda
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubcategoryPage;
